import { Component, OnInit, OnDestroy, HostListener, inject, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { RouterOutlet, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from '../environments/environment';

// MSAL imports — imported unconditionally but injected as optional
// so the app works without MSAL in local dev mode (useMsal: false)
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit, OnDestroy {
  private readonly _destroying$ = new Subject<void>();
  private router = inject(Router);
  private authService = inject(MsalService, { optional: true });
  private msalBroadcastService = inject(MsalBroadcastService, { optional: true });

  ngOnInit(): void {
    if (!environment.useMsal || !this.authService || !this.msalBroadcastService) return;

    // Handle the redirect from Microsoft login
    this.authService.handleRedirectObservable().subscribe();

    // Once interaction completes, ensure an active account is set
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        this.setActiveAccount();
      });

    // Optional: react to login/logout events for analytics or logging
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) =>
          msg.eventType === EventType.LOGIN_SUCCESS ||
          msg.eventType === EventType.LOGOUT_SUCCESS
        ),
        takeUntil(this._destroying$),
      )
      .subscribe((msg: EventMessage) => {
        if (msg.eventType === EventType.LOGIN_SUCCESS) {
          this.setActiveAccount();
        }
      });
  }

  private setActiveAccount(): void {
    if (!this.authService) return;
    const accounts = this.authService.instance.getAllAccounts();
    if (accounts.length > 0 && !this.authService.instance.getActiveAccount()) {
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleGlobalKeyboardEvent(event: KeyboardEvent) {
    if (event.altKey && event.key.toLowerCase() === 'h') {
      event.preventDefault();
      this.router.navigate(['/']);
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
